import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Card, Button, Modal, Form } from "react-bootstrap";
import { FiTruck, FiPackage, FiMapPin } from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";
import DeliveryPanelLayout from "../../../layouts/DeliveryPanelLayout";
import { SkeletonBlock } from "../../../components/general/skeleton/SkeletonBlock";
import MyContext from "../../../context/MyContext";
import "../../../assets/css/delivery/openJobs/OpenJobs.css";
import { useNavigate } from "react-router-dom";

// ── FIXED: pehle sirf "weight_kg * quantity" hota tha, jo unit=kg/g
// products ke liye hamesha "N/A" dikhata tha — seller in units ke liye
// weight_kg field bharta hi nahi (form mein hidden hoti hai), backend
// bhi (CustomerOrderController::createOrder) isi tarah calculate karta
// hai: unit=kg to quantity khud weight hai, unit=g to /1000, warna
// quantity * weight_kg. Frontend ko backend ke sath match karna tha ──
const getItemWeightKg = (cart) => {
  const product = cart.kmproduct;
  if (!product) return null;

  const unit = product.unit;
  if (unit === "kg") {
    return cart.quantity;
  }
  if (unit === "g") {
    return cart.quantity / 1000;
  }
  if (product.weight_kg) {
    return cart.quantity * product.weight_kg;
  }
  return null;
};

const OpenJobs = () => {
  const { baseUrl, baseUrlForFiles, loggedInDeliveryMan, authToken } =
    useContext(MyContext);

  // ── KYC + Availability gate — jab tak driver KYC-approved aur
  // "online" na ho, jobs list bilkul show nahi hoti ──
  const [checkingGate, setCheckingGate] = useState(true);
  const [kycStatus, setKycStatus] = useState("pending");
  const [isAvailable, setIsAvailable] = useState(false);
  const [togglingAvailability, setTogglingAvailability] = useState(false);

  // ── NEW: driver ka maujooda (pickup) city + uski service cities —
  // jobs isi "city" ke against hi match hoti hain (isDriverEligible),
  // is liye yehi field "Change Current City" se update hogi ──
  const [driverCity, setDriverCity] = useState("");
  const [driverServiceCities, setDriverServiceCities] = useState([]);
  const [showCityModal, setShowCityModal] = useState(false);
  const [changingCity, setChangingCity] = useState(false);

  const [openJobs, setOpenJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── tabs — "Open Jobs" (bid ke liye available) aur "My Bids"
  // (driver ne jin jobs pe already bid daal rakhi hai, unka status) ──
  // ── NEW: tab-switching hata di gayi — "My Bids" aur "Open Jobs"
  // ab hamesha ek sath, ek hi screen par visible hain ──
  const [myBids, setMyBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(true);

  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [quotedCharge, setQuotedCharge] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // ── checkGate — driver ka KYC status + availability status
  // fetch karta hai. Jobs list sirf tab dikhti hai jab dono theek
  // hon (KYC approved + online) — sidebar wala hi endpoint reuse
  // kiya hai (viewProfile), taake data consistent rahe.
  // FIXED: kmdeliveryman_id ab bhejne ki zaroorat nahi — backend
  // auth('Kmdeliveryman')->id() se khud driver identify karta hai ──
  const checkGate = () => {
    if (!loggedInDeliveryMan?.id || !authToken) return;
    setCheckingGate(true);
    axios
      .get(`${baseUrl}/delivery/profile/viewProfile`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setCheckingGate(false);
        if (response.data.success) {
          const d = response.data.deliveryman;
          setKycStatus((d?.is_verified || "pending").toLowerCase());
          setIsAvailable((d?.availability_status || "").toLowerCase() === "on");
          setDriverCity(d?.city || "");
          setDriverServiceCities(
            Array.isArray(d?.service_cities) ? d.service_cities : [],
          );
        }
      })
      .catch((error) => {
        setCheckingGate(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
        }
      });
  };

  // ── goOnline — sidebar (DeliverySidebar.js) ka wahi
  // availabilityStatus endpoint reuse karta hai, taake dono jagah
  // se status hamesha sync rahe ──
  const goOnline = () => {
    setTogglingAvailability(true);
    const formData = new FormData();
    formData.append("availability_status", "on");

    axios
      .post(`${baseUrl}/delivery/profile/availabilityStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setTogglingAvailability(false);
        if (response.data.success) {
          setIsAvailable(true);
          window.dispatchEvent(new Event("delivery-availability-changed"));
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to go online. Please try again.",
          });
        }
      })
      .catch((error) => {
        setTogglingAvailability(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to go online. Please try again.",
        });
      });
  };

  // ── NEW: changeCurrentCity — sirf driver ki apni service_cities
  // mein se hi select ho sakti hai (Modal isi list se options
  // banata hai). Yehi "city" field backend mein pickup-matching ke
  // liye use hoti hai (DeliveryJobBidController::isDriverEligible),
  // is liye city badalte hi Open Jobs list bhi turant refresh hoti
  // hai ──
  const changeCurrentCity = (newCity) => {
    if (!newCity || newCity === driverCity) {
      setShowCityModal(false);
      return;
    }
    setChangingCity(true);
    const formData = new FormData();
    formData.append("city", newCity);

    axios
      .post(`${baseUrl}/delivery/profile/updateProfile`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setChangingCity(false);
        if (response.data.success) {
          setDriverCity(newCity);
          setShowCityModal(false);
          Swal.fire({
            icon: "success",
            title: "City Updated",
            text: `Your current city is now set to ${newCity}.`,
            confirmButtonColor: "#097d43",
          });
          viewOpenJobs();
          viewMyBids();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message || "Failed to change city.",
          });
        }
      })
      .catch((error) => {
        setChangingCity(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to change city.",
        });
      });
  };

  useEffect(() => {
    checkGate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInDeliveryMan?.id, authToken]);

  const viewOpenJobs = () => {
    if (!authToken) {
      setLoading(false);
      navigate("/delivery/login");
      return;
    }
    if (!loggedInDeliveryMan?.id) return;
    const formData = new FormData();

    setLoading(true);
    axios
      .post(`${baseUrl}/delivery/jobbids/viewOpenJobs`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setOpenJobs(response.data.jobs || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load open jobs.",
        });
      });
  };

  const viewMyBids = () => {
    if (!loggedInDeliveryMan?.id || !authToken) return;
    const formData = new FormData();

    setLoadingBids(true);
    axios
      .post(`${baseUrl}/delivery/jobbids/viewMyBids`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setLoadingBids(false);
        if (!response.data.hasOwnProperty("error")) {
          setMyBids(response.data.bids || []);
        }
      })
      .catch((error) => {
        setLoadingBids(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
        }
      });
  };

  const gatePassed = kycStatus === "approved" && isAvailable;

  useEffect(() => {
    if (!gatePassed) return;
    viewOpenJobs();
    viewMyBids();
    const interval = setInterval(() => {
      viewOpenJobs();
      viewMyBids();
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInDeliveryMan, gatePassed]);

  const acceptAndQuote = () => {
    if (!quotedCharge || Number(quotedCharge) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Charge Required",
        text: "Please enter a valid delivery charge.",
      });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("kmdeliveryjob_id", selectedJob.id);
    formData.append("quoted_charge", quotedCharge);

    axios
      .post(`${baseUrl}/delivery/jobbids/acceptAndQuote`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setSubmitting(false);
        if (
          response.data.hasOwnProperty("error") ||
          response.data.success === false
        ) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Bid Submitted!",
            text: "Your quote has been submitted to the customer.",
            confirmButtonColor: "#097d43",
          });
          setShowQuoteModal(false);
          setQuotedCharge("");
          setSelectedJob(null);
          viewOpenJobs();
          viewMyBids();
        }
      })
      .catch((error) => {
        setSubmitting(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to submit bid.",
        });
      });
  };

  return (
    <DeliveryPanelLayout>
      <Container fluid className="open-jobs-container">
        <div className="open-jobs-header">
          <div className="open-jobs-icon-wrapper">
            <FiTruck size={28} />
          </div>
          <div>
            <h2 className="open-jobs-title">Open Jobs</h2>
            <p className="open-jobs-subtitle">
              {openJobs.length} job{openJobs.length !== 1 ? "s" : ""} available
              for bidding
            </p>
          </div>
        </div>

        {checkingGate ? (
          <p className="open-jobs-loading-text">Loading...</p>
        ) : kycStatus !== "approved" ? (
          <Card className="open-jobs-empty-card">
            <Card.Body style={{ textAlign: "center", padding: "50px 20px" }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: kycStatus === "rejected" ? "#ffebee" : "#fff8e1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  fontSize: 32,
                }}
              >
                {kycStatus === "rejected" ? "❌" : "📋"}
              </div>
              <h5 style={{ marginBottom: 8 }}>
                {kycStatus === "rejected"
                  ? "KYC Verification Rejected"
                  : "Complete Your KYC Verification"}
              </h5>
              <p style={{ color: "#6c757d", fontSize: 14, maxWidth: 380, margin: "0 auto 20px" }}>
                {kycStatus === "rejected"
                  ? "Your KYC documents were rejected. Please re-upload correct documents in Settings to start receiving job requests."
                  : "You need to submit your CNIC, vehicle details, and documents before you can see or bid on delivery jobs."}
              </p>
              <Button
                variant="success"
                onClick={() => navigate("/delivery/settings")}
              >
                Go to Settings
              </Button>
            </Card.Body>
          </Card>
        ) : !isAvailable ? (
          <Card className="open-jobs-empty-card">
            <Card.Body style={{ textAlign: "center", padding: "50px 20px" }}>
              <style>{`
                @keyframes ojs-pulse-ring {
                  0% { transform: scale(0.9); opacity: 0.7; }
                  70% { transform: scale(1.4); opacity: 0; }
                  100% { transform: scale(1.4); opacity: 0; }
                }
              `}</style>
              <div style={{ position: "relative", width: 90, height: 90, margin: "0 auto 20px" }}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "#dc3545",
                    opacity: 0.25,
                    animation: "ojs-pulse-ring 1.8s ease-out infinite",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "#dc3545",
                    opacity: 0.25,
                    animation: "ojs-pulse-ring 1.8s ease-out infinite",
                    animationDelay: "0.6s",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    background: "#ffebee",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 34,
                  }}
                >
                  🔴
                </div>
              </div>
              <h5 style={{ marginBottom: 8 }}>You're Offline</h5>
              <p style={{ color: "#6c757d", fontSize: 14, maxWidth: 340, margin: "0 auto 20px" }}>
                Turn yourself online to start seeing delivery jobs and placing bids.
              </p>
              <Button
                variant="success"
                onClick={goOnline}
                disabled={togglingAvailability}
              >
                {togglingAvailability ? "Going Online..." : "🟢 Go Online"}
              </Button>
            </Card.Body>
          </Card>
        ) : (
        <>
        {/* ══════════════════════════════════════════════════════
            NEW — City bar: driver ka maujooda pickup city + uski
            service cities dikhata hai, aur "Change Current City"
            button — jobs isi city ke against match hoti hain.
           ══════════════════════════════════════════════════════ */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            background: "#f8f9fa",
            border: "1px solid #eee",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 13.5 }}>
            <p style={{ margin: 0 }}>
              <FiMapPin size={13} className="me-1" /> <strong>Current City:</strong>{" "}
              {driverCity || "Not set"}
            </p>
            <p style={{ margin: "4px 0 0", color: "#6c757d" }}>
              You provide services in:{" "}
              {driverServiceCities.length > 0
                ? driverServiceCities.join(", ")
                : "No service cities added yet"}
            </p>
          </div>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => setShowCityModal(true)}
          >
            Change Current City
          </Button>
        </div>

        {/* ══════════════════════════════════════════════════════
            NEW — Side-by-side 6-6 columns. "Open Jobs" left, "My
            Bids" right. Bootstrap Col automatically stacks on
            mobile (full-width), so no separate mobile layout needed.
           ══════════════════════════════════════════════════════ */}
        <Row>
          <Col md={6}>
            {/* ── OPEN JOBS SECTION ── */}
            <h5 style={{ fontWeight: 700, marginBottom: 12, marginTop: 4 }}>
              Open Jobs ({openJobs.length})
            </h5>

            {loading ? (
              [1, 2, 3].map((i) => (
                <Card key={i} className="open-job-card">
                  <Card.Body>
                    <div className="open-job-layout">
                      <div className="open-job-info" style={{ width: '100%' }}>
                        <SkeletonBlock width="120px" height="14px" borderRadius="4px" />
                        <div className="open-job-location-row" style={{ marginTop: '12px' }}>
                          <SkeletonBlock width="60px" height="20px" borderRadius="50px" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <SkeletonBlock width="55%" height="14px" borderRadius="4px" />
                            <SkeletonBlock width="80%" height="12px" borderRadius="4px" className="skel-mt-sm" />
                          </div>
                        </div>
                        <div className="open-job-location-row" style={{ marginTop: '10px' }}>
                          <SkeletonBlock width="60px" height="20px" borderRadius="50px" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <SkeletonBlock width="45%" height="14px" borderRadius="4px" />
                            <SkeletonBlock width="70%" height="12px" borderRadius="4px" className="skel-mt-sm" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))
            ) : openJobs.length === 0 ? (
              <Card className="open-jobs-empty-card">
                <Card.Body>
                  <p className="open-jobs-empty-text">
                    No open jobs available at the moment. New orders will appear here as they come in.
                  </p>
                </Card.Body>
              </Card>
            ) : (
              openJobs.map((job) => (
                <Card key={job.id} className="open-job-card">
                  <Card.Body>
                    <div className="open-job-layout">
                      <div className="open-job-info">
                        <p className="open-job-order-id">Order #{job.kmorder_id}</p>

                        <div className="open-job-location-row">
                          <span className="open-job-location-badge pickup">
                            Pickup
                          </span>
                          <div>
                            <p className="open-job-location-name">
                              {job.kmorder?.kmseller?.shop_name}
                            </p>
                            <p className="open-job-location-address">
                              {job.kmorder?.kmseller?.kmbusinessdetails?.address ||
                                "Address not available"}
                            </p>
                          </div>
                        </div>

                        <div className="open-job-location-row">
                          <span className="open-job-location-badge dropoff">
                            Drop-off
                          </span>
                          <div>
                            <p className="open-job-location-name">
                              {job.kmcustomer?.name}
                            </p>
                            <p className="open-job-location-address">
                              {job.kmorder?.shippingAddress}
                            </p>
                          </div>
                        </div>

                        <div className="open-job-products">
                          {job.kmorder?.kmcarts?.length > 0 ? (
                            job.kmorder.kmcarts.map((cart, idx) => {
                              const itemWeight = getItemWeightKg(cart);
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    marginBottom: "10px",
                                    padding: "8px",
                                    border: "1px solid #eee",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <img
                                    src={`${baseUrlForFiles}/${cart.kmproduct?.image}`}
                                    alt={cart.kmproduct?.name}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                    }}
                                  />

                                  <div style={{ flex: 1 }}>
                                    <strong>
                                      {cart.kmproduct?.name || "Product"}
                                    </strong>

                                    <div style={{ fontSize: "13px", color: "#666" }}>
                                      Quantity : {cart.quantity} {cart.kmproduct?.unit || ""}
                                    </div>

                                    <div style={{ fontSize: "13px", color: "#666" }}>
                                      Weight :{" "}
                                      {itemWeight != null
                                        ? `${itemWeight.toFixed(2)} kg`
                                        : "N/A"}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <p>No products available.</p>
                          )}
                        </div>

                        <div className="open-job-meta">
                          <span className="open-job-weight">
                            <FiPackage size={13} /> Total Weight :{" "}
                            {job.total_weight} kg
                          </span>
                        </div>
                      </div>
                      <Button
                        variant={job.has_bid ? "secondary" : "success"}
                        className="accept-quote-btn"
                        disabled={job.has_bid}
                        onClick={() => {
                          if (job.has_bid) return;
                          setSelectedJob(job);
                          setQuotedCharge("");
                          setShowQuoteModal(true);
                        }}
                      >
                        {job.has_bid ? "Bid Already Submitted" : "Accept & Quote"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}
          </Col>

          <Col md={6}>
            {/* ── MY BIDS SECTION ── */}
            <h5 style={{ fontWeight: 700, marginBottom: 12, marginTop: 4 }}>
              My Bids ({myBids.length})
            </h5>

        {loadingBids ? (
          [1, 2].map((i) => (
            <Card key={i} className="open-job-card">
              <Card.Body>
                <div className="open-job-layout">
                  <div className="open-job-info" style={{ width: '100%' }}>
                    <SkeletonBlock width="120px" height="14px" borderRadius="4px" />
                    <div className="open-job-location-row" style={{ marginTop: '12px' }}>
                      <SkeletonBlock width="60px" height="20px" borderRadius="50px" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SkeletonBlock width="55%" height="14px" borderRadius="4px" />
                        <SkeletonBlock width="80%" height="12px" borderRadius="4px" className="skel-mt-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))
        ) : myBids.length === 0 ? (
          <Card className="open-jobs-empty-card">
            <Card.Body>
              <p className="open-jobs-empty-text">
                You have not placed any bids yet.
              </p>
            </Card.Body>
          </Card>
        ) : (
          myBids.map((bid) => (
            <Card key={bid.id} className="open-job-card">
              <Card.Body>
                <div className="open-job-layout">
                  <div className="open-job-info">
                    <p className="open-job-order-id">
                      Order #{bid.kmdeliveryjob?.kmorder_id}
                    </p>

                    <div className="open-job-location-row">
                      <span className="open-job-location-badge pickup">
                        Pickup
                      </span>
                      <div>
                        <p className="open-job-location-name">
                          {bid.kmdeliveryjob?.kmorder?.kmseller?.shop_name}
                        </p>
                        <p className="open-job-location-address">
                          {bid.kmdeliveryjob?.kmorder?.kmseller?.kmbusinessdetails?.address ||
                            "Address not available"}
                        </p>
                      </div>
                    </div>

                    <div className="open-job-location-row">
                      <span className="open-job-location-badge dropoff">
                        Drop-off
                      </span>
                      <div>
                        <p className="open-job-location-address">
                          {bid.kmdeliveryjob?.kmorder?.shippingAddress}
                        </p>
                      </div>
                    </div>

                    <div className="open-job-meta">
                      <span className="open-job-weight">
                        <FiPackage size={13} /> Your Quote: Rs. {Number(bid.quoted_charge).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "6px 14px",
                      borderRadius: 20,
                      background: "#fff8e1",
                      color: "#b8860b",
                      fontWeight: 600,
                      fontSize: 13,
                      height: "fit-content",
                    }}
                  >
                    Waiting for Customer
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))
        )}
          </Col>
        </Row>
        </>
        )}

        {/* ══════════════════════════════════════════════════════
            NEW — Change Current City modal. Options list sirf
            driver ki apni service_cities se banti hai, koi bhi
            random shehar select nahi kar sakta ──
           ══════════════════════════════════════════════════════ */}
        <Modal
          show={showCityModal}
          onHide={() => setShowCityModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Change Current City</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: 13, color: "#6c757d" }}>
              Select a city from your service cities. This becomes your pickup
              city — jobs will be matched against it.
            </p>
            {driverServiceCities.length === 0 ? (
              <p style={{ fontSize: 13 }}>
                You haven't added any service cities yet. Go to Settings →
                Delivery Cities to add some first.
              </p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {driverServiceCities.map((c) => (
                  <Button
                    key={c}
                    variant={c === driverCity ? "success" : "outline-success"}
                    size="sm"
                    disabled={changingCity}
                    onClick={() => changeCurrentCity(c)}
                  >
                    {c}
                  </Button>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCityModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showQuoteModal}
          onHide={() => setShowQuoteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Quote Your Delivery Charge</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedJob && (
              <div style={{ marginBottom: 16 }}>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#097d43",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 8,
                  }}
                >
                  Order #{selectedJob.kmorder_id} — Products
                </p>
                {selectedJob.kmorder?.kmcarts?.length > 0 ? (
                  selectedJob.kmorder.kmcarts.map((cart, idx) => {
                    const itemWeight = getItemWeightKg(cart);
                    return (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "6px 0",
                          borderBottom:
                            idx < selectedJob.kmorder.kmcarts.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                        }}
                      >
                        <img
                          src={`${baseUrlForFiles}/${cart.kmproduct?.image}`}
                          alt={cart.kmproduct?.name}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: 6,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>
                            {cart.kmproduct?.name || "Product"}
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: "#8a9088" }}>
                            Qty: {cart.quantity} {cart.kmproduct?.unit || ""}
                            {itemWeight != null
                              ? ` · ${itemWeight.toFixed(2)} kg`
                              : ""}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p style={{ fontSize: 13, color: "#8a9088" }}>No products available.</p>
                )}
                <p style={{ fontSize: 13, fontWeight: 600, marginTop: 8, marginBottom: 0 }}>
                  <FiPackage size={13} className="me-1" /> Total Weight: {selectedJob.total_weight} kg
                </p>
              </div>
            )}

            <Form.Label>Delivery Charge (Rs.)</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 250"
              value={quotedCharge}
              onChange={(e) => setQuotedCharge(e.target.value)}
            />
            <p className="quote-modal-note">
              This is the amount you will charge the customer for delivering this order. Please quote a fair price based on distance, weight, and effort.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowQuoteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={acceptAndQuote}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Bid"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </DeliveryPanelLayout>
  );
};

export default OpenJobs;
